import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import GaugeComponent from 'react-gauge-component';
import { ReadingDataApi } from '../../API/ReadingDataApi';
import LootBox from '../../Components/LootBox';
import { READING_SPEED_THRESHOLDS_BY_LEXILE } from '../../Constants/lexiles';
import { Currency } from '../../Context/BalanceProvider';
import { useSession } from '../../Context/ReadingSessionProvider';
import { XPRewardCard } from '../../Rewards/XPRewardCard';
import { Article } from '../../Types/Article';
import { Transaction } from '../../Types/Money';
import { QuestionResponse } from '../../Types/QuestionResponse';
import { UserArticleReadingStats } from '../../Types/UserArticleReadingStats';
import { UserLootBox } from '../../Types/UserLootBox';
import QuizQuestionResultExplanation from './QuizQuestionResultExplanation';

interface QuizResultSummaryProps {
  article: Article | null;
  guidingQuestionAnswers: QuestionResponse[];
  assessmentQuestionAnswers: QuestionResponse[];
  lootBox: UserLootBox | null | undefined;
  showLootBox: boolean;
  rewards: Transaction[];
}

const QuizResultSummary: React.FC<QuizResultSummaryProps> = ({
  article,
  guidingQuestionAnswers,
  assessmentQuestionAnswers,
  lootBox,
  showLootBox,
  rewards,
}) => {
  const { subscribeToActivityPings } = useSession();
  const [articleReadingStats, setArticleReadingStats] =
    useState<UserArticleReadingStats>();
  const [isAllActivityDataCollected, setIsAllActivityDataCollected] =
    useState(false);

  const xpReward = rewards?.find((reward) => reward.currency === Currency.XP);

  useEffect(() => {
    const stopTrackingActivity = subscribeToActivityPings(
      async ({ timestamp, idleTime, activeTime }) => {
        if (!activeTime) {
          console.debug('No Reading Activity to record');
          return;
        }

        // At this point we know that activity data was recorded on the server (by ReadArticleView)
        setIsAllActivityDataCollected(true);
      },
    );

    return () => {
      stopTrackingActivity?.();
    };
  }, []);

  useEffect(() => {
    const readingDataApi = new ReadingDataApi();

    const fetchArticleReadingStats = async () => {
      try {
        const readingStats = await readingDataApi.fetchArticleStats(article.id);

        setArticleReadingStats(readingStats);
      } catch (error) {
        console.error('Error fetching article stats:', error);
      }
    };

    fetchArticleReadingStats();
  }, [isAllActivityDataCollected, article?.id]);

  const accuracy = useMemo(() => {
    return (
      (assessmentQuestionAnswers.filter(
        (answer) => answer.answerState === 'correct',
      ).length /
        assessmentQuestionAnswers.length) *
      100
    );
  }, [assessmentQuestionAnswers]);

  interface ReadingPerformanceStatsProps {
    accuracy: number;
    wordsPerMinute: number;
    readingSpeedThreshold: { ok: number; good: number };
  }

  const ReadingPerformanceStats: React.FC<ReadingPerformanceStatsProps> = ({
    accuracy,
    wordsPerMinute,
    readingSpeedThreshold,
  }) => {
    const readingSpeedThresholdIdeal = Math.round(
      (readingSpeedThreshold.good * 4) / 3,
    );

    return (
      <div className="flex flex-row flex-wrap justify-center items-center py-4 w-full">
        <div className="flex flex-col items-center max-w-[250px] max-sm:w-1/2 xl:w-1/2">
          <h4 className="text-black text-base font-bold">Quiz Accuracy</h4>
          <div className="w-full">
            <GaugeComponent
              type="semicircle"
              value={accuracy}
              minValue={0}
              maxValue={100}
              style={{ width: '100%', height: 'auto' }}
              arc={{
                width: 0.3,
                padding: 0.009,
                cornerRadius: 0,
                subArcs: [
                  {
                    limit: 40,
                    color: accuracy < 40 ? '#dc3534' : '#c6c9cb',
                  },
                  {
                    limit: 80,
                    color:
                      accuracy >= 40 && accuracy <= 80 ? '#ff8900' : '#c6c9cb',
                  },
                  {
                    color: accuracy > 80 ? '#198754' : '#c6c9cb',
                  },
                ],
              }}
              pointer={{
                color: '#71767a',
                length: 0.65,
                width: 15,
                type: 'needle',
                animate: false,
              }}
              labels={{
                valueLabel: {
                  formatTextValue: formatPercentage,
                  matchColorWithArc: true,
                  style: {
                    fontSize: '24rem',
                    textShadow: 'none',
                    fontWeight: 'bold',
                    transform: 'translateY(1.4em)',
                  },
                  maxDecimalDigits: 0,
                },
                tickLabels: {
                  type: 'outer',
                  defaultTickValueConfig: {
                    formatTextValue: formatPercentage,
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                  ticks: getReadingAccuracyTicks(),
                },
              }}
            />
          </div>
        </div>
        <div className="flex flex-col items-center max-w-[250px] max-sm:w-1/2 xl:w-1/2">
          <h4 className="text-black text-base font-bold">Words Per Minute</h4>
          <div className="w-full">
            <GaugeComponent
              type="semicircle"
              value={wordsPerMinute}
              minValue={0}
              maxValue={readingSpeedThresholdIdeal}
              style={{ width: '100%', height: 'auto' }}
              arc={{
                width: 0.3,
                padding: 0.009,
                cornerRadius: 0,
                subArcs: [
                  {
                    limit: (6 / 13) * readingSpeedThresholdIdeal, // readingSpeedThresholdIdeal * 0.45, TODO for now as it looks cleaner. 6/13 is a 46.1% so not noticeably different
                    color:
                      wordsPerMinute < readingSpeedThreshold.ok
                        ? '#dc3534'
                        : '#c6c9cb',
                  },
                  {
                    limit: (10 / 13) * readingSpeedThresholdIdeal, // readingSpeedThresholdIdeal * 0.75, TODO for now as it looks cleaner. 10/13 is a 76.9% so not noticeably different
                    color:
                      wordsPerMinute >= readingSpeedThreshold.ok &&
                      wordsPerMinute < readingSpeedThreshold.good
                        ? '#ff8900'
                        : '#c6c9cb',
                  },
                  {
                    color:
                      wordsPerMinute > readingSpeedThreshold.good
                        ? '#198754'
                        : '#c6c9cb',
                  },
                ],
              }}
              pointer={{
                color: '#71767a',
                length: 0.65,
                width: 15,
                type: 'needle',
                animate: false,
              }}
              labels={{
                valueLabel: {
                  formatTextValue: formatWordsPerMinute,
                  // This logic plus the optional fill below is needed because the component has a bug,
                  // if value is bigger than max (something common in gauges) the color snaps back to white
                  // instead of picking the color from the last arc :)
                  matchColorWithArc:
                    wordsPerMinute < readingSpeedThresholdIdeal,
                  style: {
                    fontSize: '48rem',
                    textShadow: 'none',
                    fontWeight: 'bold',
                    transform: 'translateY(1.4em)',
                    ...(wordsPerMinute < readingSpeedThresholdIdeal
                      ? {}
                      : { fill: '#198754' }),
                  },
                  maxDecimalDigits: 0,
                },
                tickLabels: {
                  type: 'outer',
                  defaultTickValueConfig: {
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                  ticks: getReadingSpeedTicks(
                    readingSpeedThresholdIdeal,
                    readingSpeedThreshold,
                  ),
                },
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  function getReadingSpeedTicks(
    readingSpeedThresholdIdeal: number,
    readingSpeedThreshold: { ok: number; good: number },
  ) {
    return [
      ...[...Array(5).keys()].map((i) => ({
        value: ((i + 1) / 13) * readingSpeedThresholdIdeal,
        valueConfig: { formatTextValue: () => '' },
      })),
      {
        value: (6 / 13) * readingSpeedThresholdIdeal,
        valueConfig: { formatTextValue: () => `${readingSpeedThreshold.ok}` },
      },
      ...[...Array(4).keys()].map((i) => ({
        value: ((6 + i) / 13) * readingSpeedThresholdIdeal,
        valueConfig: { formatTextValue: () => '' },
      })),
      {
        value: (10 / 13) * readingSpeedThresholdIdeal,
        valueConfig: { formatTextValue: () => `${readingSpeedThreshold.good}` },
      },
      ...[...Array(3).keys()].map((i) => ({
        value: ((10 + i) / 13) * readingSpeedThresholdIdeal,
        valueConfig: { formatTextValue: () => '' },
      })),
      {
        value: readingSpeedThresholdIdeal,
        valueConfig: {
          formatTextValue: () => `${readingSpeedThresholdIdeal}+`,
        },
      },
    ];
  }

  function getReadingAccuracyTicks() {
    // TODO For both tick generators we could extract some constants.
    return [
      ...[20, 60].map((i) => ({
        value: i,
        valueConfig: { formatTextValue: () => '' },
      })),
      { value: 40 },
      { value: 80 },
    ];
  }

  function formatPercentage(decimal: number) {
    return `${Math.floor(decimal)}%`;
  }

  function formatWordsPerMinute(wordsPerMinute: number) {
    return `${wordsPerMinute} Words/Min`;
  }

  return (
    <>
      <div className="items-stretch flex w-full flex-col px-5 max-lg:px-3 max-xl:max-w-full">
        {showLootBox ? <LootBox lootBox={lootBox} /> : null}
        <div className="flex flex-row flex-wrap justify-center gap-4 w-full">
          {/* Show XP loader when loading finished article. Only show the standard rewards animation on quiz completion. */}
          <XPRewardCard
            isLoading={!showLootBox ? !rewards : false}
            value={xpReward?.value}
          />
        </div>

        <ReadingPerformanceStats
          accuracy={accuracy}
          wordsPerMinute={articleReadingStats?.wordsPerMinuteRead ?? 0}
          readingSpeedThreshold={
            READING_SPEED_THRESHOLDS_BY_LEXILE[article.lexileLevel]
          }
        />

        <div className="justify-between items-stretch flex mt-6 gap-5 max-md:max-w-full max-md:flex-wrap">
          <div className="text-black text-base font-semibold grow shrink basis-auto self-center">
            Guiding Questions
          </div>
          <div
            data-testid="guiding-questions-score"
            className="text-green-700 text-base font-semibold whitespace-nowrap self-center border border-[color:var(--informational-success,#198754)] bg-green-700 bg-opacity-10 px-2 py-1.5 rounded-md border-solid"
          >
            Score:{' '}
            {
              guidingQuestionAnswers.filter(
                (answer) => answer.answerState === 'correct',
              ).length
            }
            /{guidingQuestionAnswers.length}
          </div>
        </div>
        {guidingQuestionAnswers.map((answer, index) => (
          <QuizQuestionResultExplanation
            key={answer.questionId}
            article={article}
            questionResponse={answer}
          />
        ))}
        <div className="justify-between items-stretch flex gap-5 mt-8 max-md:max-w-full max-md:flex-wrap">
          <div className="text-black text-base font-semibold self-center">
            Quiz Questions
          </div>
          <div
            data-testid="quiz-questions-score"
            className="text-green-700 text-base font-semibold whitespace-nowrap self-center border border-[color:var(--informational-success,#198754)] bg-green-700 bg-opacity-10 px-2 py-1.5 rounded-md border-solid"
          >
            Score:{' '}
            {
              assessmentQuestionAnswers.filter(
                (answer) => answer.answerState === 'correct',
              ).length
            }
            /{assessmentQuestionAnswers.length}
          </div>
        </div>
        {assessmentQuestionAnswers.map((answer, index) => (
          <QuizQuestionResultExplanation
            key={answer.questionId}
            article={article}
            questionResponse={answer}
          />
        ))}
      </div>
    </>
  );
};

export default QuizResultSummary;
